import type React from 'react';

import { Chip } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core-react/i18n';

import { formatGoal, GOAL_PRESETS } from '../../constants';

type Props = {
	value: number | undefined;
	onChange: (value: number | undefined) => void;
	ariaLabelledBy: string;
};

/**
 * `@change/ds-forms`'s Radio/RadioGroup renders a fixed dot-radio look with no chip styling hook,
 * and `Chip`/`ChipGroup` (`@change/ds-core-ui`) have no built-in selection/keyboard semantics --
 * neither DS component alone covers "chip-styled, zero-or-one-selected group". Modeled as a group
 * of toggle buttons (`aria-pressed`) rather than a radiogroup: unlike a radio, a toggle button can
 * legitimately go back to unpressed when reactivated, which matches tapping the active chip to
 * clear the goal. Chip forwards unrecognized props straight to its underlying button via
 * react-aria's mergeProps, so `aria-pressed`/`aria-label` below are layered on top of Chip's own
 * documented props (active/onClick/disabled), not invented ones. Every chip is a native Tab stop
 * (Chip's `useButton()` always asserts its own `tabIndex`), so no extra keyboard handling is added
 * here -- matches the established `Chip` + `aria-pressed` toggle-group pattern elsewhere in this
 * repo (`InitialLetterFilter`, `StateCodeFilter`). The visible label is just the bare number (per
 * Figma), so the full "N signers" plural still reaches assistive tech via `aria-label` instead of
 * the chip's visible text content. `ChipGroup` isn't used here: it only lays chips out as a
 * wrapping flex row sized to content (its stories are all pill-shaped tag rows), but Figma calls
 * for four equal-width, fixed-height squares -- a plain CSS grid on the group wrapper, with each
 * Chip stretched to fill its cell.
 */
export const GoalPresetChips: React.FC<Props> = ({ value, onChange, ariaLabelledBy }) => {
	const { translatePlural } = useI18n();

	return (
		<fieldset aria-labelledby={ariaLabelledBy} className="m-0 grid grid-cols-4 gap-x-2.5 gap-y-2 border-0 p-0">
			{GOAL_PRESETS.map((goalValue) => {
				const isSelected = goalValue === value;
				return (
					<Chip
						key={goalValue}
						size="large"
						active={isSelected}
						aria-pressed={isSelected}
						aria-label={translatePlural('corgi.psf.share.sharing_goal.chips.chip_label', goalValue, {
							count: formatGoal(goalValue),
						})}
						onClick={() => onChange(isSelected ? undefined : goalValue)}
						data-qa={`sharing-goal-chip-${goalValue}`}
						className="h-16 w-full justify-center"
					>
						{formatGoal(goalValue)}
					</Chip>
				);
			})}
		</fieldset>
	);
};
